import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, LogIn, Moon, Sun, Monitor, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
const SithabileLogo = '/sithabile-fingerprint-logo.png'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
})

type LoginFormData = z.infer<typeof loginSchema>

interface LoginNotice {
  tone: 'error' | 'success'
  text: string
}

/** Messages shown above the form for the `?error=`/`?invited=` params SSO and invites redirect with. */
const LOGIN_NOTICES: Record<string, LoginNotice> = {
  'error=not_invited': {
    tone: 'error',
    text: 'This dashboard is invite-only. Ask an administrator for an invitation.',
  },
  'error=account_disabled': {
    tone: 'error',
    text: 'This account has been deactivated. Contact an administrator.',
  },
  'error=sso_failed': {
    tone: 'error',
    text: 'Single sign-on failed. Please try again or use your password.',
  },
  'invited=1': {
    tone: 'success',
    text: 'Account created — sign in to continue.',
  },
}

export function LoginPage() {
  const { login, user, isLoading: authLoading } = useAuth()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const loginNotice =
    LOGIN_NOTICES[`error=${searchParams.get('error')}`] ??
    LOGIN_NOTICES[`invited=${searchParams.get('invited')}`] ??
    null
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [authProviders, setAuthProviders] = useState<any[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  })

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      // Check for stored return path first, then location state, then default to dashboard
      const returnPath = localStorage.getItem('returnPath')
      const from = returnPath || (location.state as any)?.from?.pathname || '/dashboard'
      
      // Clear the stored return path
      if (returnPath) {
        localStorage.removeItem('returnPath')
      }
      
      navigate(from, { replace: true })
    }
  }, [user, authLoading, navigate, location])

  useEffect(() => {
    // Check for available auth providers
    const checkAuthProviders = async () => {
      try {
        const response = await fetch('/api/v1/auth-providers/public')
        if (response.ok) {
          const providers = await response.json()
          // Add the Microsoft icon to active providers
          const activeProviders = providers.map((p: any) => ({
            ...p,
            icon: p.provider_type === 'microsoft' ? (
              <svg className="w-5 h-5" viewBox="0 0 23 23">
                <path fill="#f3f3f3" d="M0 0h10.931v10.931H0z"/>
                <path fill="#f35325" d="M12.069 0H23v10.931H12.069z"/>
                <path fill="#05a6f0" d="M0 12.069h10.931V23H0z"/>
                <path fill="#ffba08" d="M12.069 12.069H23V23H12.069z"/>
              </svg>
            ) : null
          }))
          setAuthProviders(activeProviders)
        }
      } catch (error) {
        console.error('Failed to fetch auth providers:', error)
      }
    }
    checkAuthProviders()
  }, [])

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    try {
      await login(data.email, data.password, data.rememberMe)
      // Navigation and success toast are handled in AuthContext
    } catch (error: any) {
      toast.error(error.message || 'Invalid credentials')
    } finally {
      setIsLoading(false)
    }
  }

  const themeIcons = {
    light: <Sun className="w-4 h-4" />,
    dark: <Moon className="w-4 h-4" />,
    system: <Monitor className="w-4 h-4" />,
  }

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#5869ff]/30 border-t-[#5869ff] rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated gradient background - Sithabile color scheme */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 dark:from-[#000742] dark:via-[#1a1f5c] dark:to-[#2a2f6c]">
        <div className="absolute inset-0">
          {/* Animated gradient orbs - Sithabile colors */}
          <motion.div
            className="absolute top-0 -left-4 w-72 h-72 bg-[#5869ff] dark:bg-[#5869ff] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-40"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <motion.div
            className="absolute top-0 -right-4 w-72 h-72 bg-[#f8724f] dark:bg-[#f8724f] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-30"
            animate={{
              x: [0, -100, 0],
              y: [0, 100, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <motion.div
            className="absolute -bottom-8 left-20 w-72 h-72 bg-[#000742] dark:bg-[#5869ff] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-50"
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
            }}
            transition={{
              duration: 22,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <motion.div
            className="absolute bottom-0 right-20 w-72 h-72 bg-[#f8724f] dark:bg-[#f8724f] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-40"
            animate={{
              x: [0, -100, 0],
              y: [0, -100, 0],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          
          {/* Additional floating elements for depth */}
          <motion.div
            className="absolute top-1/2 left-1/3 w-48 h-48 bg-[#5869ff] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-2xl opacity-20"
            animate={{
              x: [0, 50, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <motion.div
            className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-[#000742] dark:bg-[#f8724f] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-2xl opacity-25"
            animate={{
              x: [0, -60, 0],
              y: [0, 40, 0],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        </div>
        
        {/* Geometric pattern overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='50' cy='50' r='1' fill='%23000742' opacity='0.5'/%3E%3Ccircle cx='0' cy='0' r='1' fill='%23000742' opacity='0.5'/%3E%3Ccircle cx='100' cy='0' r='1' fill='%23000742' opacity='0.5'/%3E%3Ccircle cx='0' cy='100' r='1' fill='%23000742' opacity='0.5'/%3E%3Ccircle cx='100' cy='100' r='1' fill='%23000742' opacity='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`
        }}></div>
      </div>

      {/* Theme toggle button */}
      <div className="absolute top-4 right-4 z-20">
        <div className="flex items-center gap-2 p-1 rounded-lg bg-white/80 dark:bg-[#000742]/80 backdrop-blur-sm border border-gray-200 dark:border-[#5869ff]/30">
          {(['light', 'dark', 'system'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTheme(t)}
              className={`p-2 rounded-md transition-colors ${
                theme === t
                  ? 'bg-[#5869ff] text-white'
                  : 'hover:bg-gray-100 dark:hover:bg-[#1a1f5c] text-gray-700 dark:text-gray-300'
              }`}
              title={`${t.charAt(0).toUpperCase() + t.slice(1)} theme`}
            >
              {themeIcons[t]}
            </button>
          ))}
        </div>
      </div>

      {/* Login card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-white/90 dark:bg-[#000742]/90 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20 dark:border-[#5869ff]/20">
          {/* Logo and title */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
              className="mb-6"
            >
              <img src={SithabileLogo} alt="Sithabile" className="h-16 mx-auto" />
            </motion.div>
            <h1 className="text-2xl font-bold text-[#000742] dark:text-white">
              NetApp Dashboard
            </h1>
            <p className="text-[#475467] dark:text-gray-400 mt-2">
              Enterprise Storage Monitoring
            </p>
          </div>

          {loginNotice && (
            <div
              role={loginNotice.tone === 'error' ? 'alert' : 'status'}
              className={`mb-6 p-3 rounded-lg text-sm ${
                loginNotice.tone === 'error'
                  ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300'
                  : 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300'
              }`}
            >
              {loginNotice.text}
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email field */}
            <div>
              <label className="block text-sm font-medium text-[#344054] dark:text-gray-300 mb-2">
                Email Address
              </label>
              <input
                {...register('email')}
                type="email"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white/50 dark:bg-[#1a1f5c]/30 backdrop-blur-sm focus:ring-2 focus:ring-[#5869ff] focus:border-transparent transition-all text-gray-900 dark:text-white"
                placeholder="admin@netapp.com"
                autoComplete="email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-[#f8724f]">{errors.email.message}</p>
              )}
            </div>

            {/* Password field */}
            <div>
              <label className="block text-sm font-medium text-[#344054] dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white/50 dark:bg-[#1a1f5c]/30 backdrop-blur-sm focus:ring-2 focus:ring-[#5869ff] focus:border-transparent transition-all pr-12 text-gray-900 dark:text-white"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475467] hover:text-[#344054] dark:text-gray-400 dark:hover:text-gray-300"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-[#f8724f]">{errors.password.message}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  {...register('rememberMe')}
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-[#5869ff] focus:ring-[#5869ff]"
                />
                <span className="ml-2 text-sm text-[#344054] dark:text-gray-300">
                  Remember me
                </span>
              </label>
              <a
                href="#"
                className="text-sm text-[#5869ff] hover:text-[#f8724f] dark:text-[#5869ff] dark:hover:text-[#f8724f] transition-colors"
              >
                Forgot password?
              </a>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg font-medium hover:from-[#f8724f] hover:to-[#e85d3a] focus:outline-none focus:ring-2 focus:ring-[#5869ff] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* SSO options - only show if providers are configured */}
          {authProviders.length > 0 && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-[#5869ff]/30" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white/80 dark:bg-[#000742]/80 text-[#475467]">
                    Or continue with
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {authProviders.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={async () => {
                      try {
                        const response = await fetch(`/api/v1/auth/sso/${provider.provider_type}/login`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          credentials: 'include',
                          body: JSON.stringify({ provider_id: provider.id })
                        })
                        const data = await response.json()
                        if (data.auth_url) {
                          window.location.href = data.auth_url
                        } else {
                          toast.error('SSO not configured properly')
                        }
                      } catch (error) {
                        console.error('Failed to initiate SSO:', error)
                        toast.error('Failed to initiate SSO login')
                      }
                    }}
                    className="w-full py-3 px-4 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg text-[#344054] dark:text-gray-300 hover:bg-[#f8724f]/10 dark:hover:bg-[#5869ff]/10 transition-all transform hover:scale-[1.01] flex items-center justify-center gap-3"
                  >
                    {provider.icon}
                    <span className="font-medium">Continue with {provider.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}